<script setup lang="ts">
import { computed, onBeforeUpdate, ref } from 'vue'
import { sanitizeMessage, useMessages } from '../store/useMessages'
import { useFormatDate } from '../composables/formatDate'
import { useRoute } from 'vue-router'
import BannerOffline from '../components/BannerOffline.vue'
import FormMessage from '../components/FormMessage.vue'
import AttachmentLink from '../components/message/AttachmentLink.vue'

const { message: getMessage, deleteMessage, send } = useMessages()
const route = useRoute()
const messageID = ref(Number(route.params.id))
const message = ref(getMessage(messageID.value))
onBeforeUpdate(() => {
  messageID.value = Number(route.params.id)
  message.value = getMessage(messageID.value)
})

const renderBody = computed(() => {
  return sanitizeMessage(message.value.message.body)
})
const renderDate = computed(() => {
  const messageDate = useFormatDate(message.value.message.date)
  const dateTime = `${messageDate.weekday()}, ${messageDate.month()} ${messageDate.day()}, ${messageDate.time()}`
  if (
    new Date().getDate() == message.value.message.date.getDate() &&
    new Date().getMonth() == message.value.message.date.getMonth() &&
    new Date().getFullYear() == message.value.message.date.getFullYear()
  ) {
    return `${dateTime}` //format Monday, July 18, 12:02 PM
  } else {
    const today = new Date()
    const oneDay = 24 * 60 * 60 * 1000 // hours*minutes*seconds*milliseconds
    const days = Math.round(
      Math.abs((today.getTime() - message.value.message.date.getTime()) / oneDay)
    )
    if (days < 2) {
      return `${dateTime} (${days} day ago)` //format Monday, July 18, 12:02 PM (4 days ago)
    } else {
      return `${dateTime} (${days} days ago)` //format Monday, July 18, 12:02 PM (4 days ago)
    }
  }
})

const modalConfirmation = ref(false)
const modalReplyMessage = ref(false)
</script>
<template>
  <BannerOffline data-test="pageInventory">
    You are offline, messages will be sent/recieved when the device is connected.
  </BannerOffline>
  <q-page v-if="message.message">
    <div class="q-pa-md">
      <div class="q-gutter-sm q-mb-sm">
        <q-btn
          color="white"
          text-color="grey-7"
          class="no-padding"
          flat
          rounded
          no-caps
          icon="arrow_back"
          label="Back"
          to="/messaging"
        />
      </div>
      <div class="row no-wrap justify-between q-col-gutter-x-md">
        <div class="row items-center">
          <h1 class="text-h4 text-weight-medium">
            {{ message.message.subject }}
          </h1>
        </div>
        <div class="text-center column">
          <q-badge
            v-if="message.message.priority"
            color="warning"
            class="text-black q-py-xs text-body2"
          >
            <q-icon name="priority_high" class="q-mr-xs" />
            High Priority
          </q-badge>
        </div>
      </div>
      <div class="text-body2 q-mt-xs row justify-between">
        <div>
          <span v-if="message.sent">To: </span>
          <strong>{{ message.sent ? message.message.to.name : message.message.from.name }}</strong>
          <span class="text-grey-7">
            &lt;{{
              message.sent ? message.message.to.username : message.message.from.username
            }}&gt;</span
          >
        </div>
        <div class="text-grey-7">{{ renderDate }}</div>
      </div>
      <div class="text-body1 q-gutter-md q-mt-md">
        <p v-html="renderBody"></p>
      </div>
      <div v-if="message.message.attachment" class="q-my-sm">
        <template v-for="(attachment, index) in message.message.attachments" :key="index">
          <AttachmentLink :attachment="attachment" />
        </template>
      </div>
      <div class="q-gutter-sm q-my-sm">
        <q-btn color="primary" rounded no-caps label="Reply" @click="modalReplyMessage = true" />
        <q-btn
          color="negative"
          flat
          rounded
          no-caps
          label="Delete"
          @click="modalConfirmation = true"
        />
      </div>
    </div>
  </q-page>

  <q-dialog v-model="modalReplyMessage">
    <q-card style="width: 480px; max-width: 80vw" class="q-pa-md">
      <FormMessage
        reply
        :message="message"
        :sent="message.sent"
        @send="send($event), $router.push('/messaging')"
      />
    </q-card>
  </q-dialog>

  <q-dialog v-model="modalConfirmation">
    <q-card style="width: 320px; max-width: 80vw" class="q-pa-md">
      <q-card-section>
        <h5 class="text-h6">Delete this message?</h5>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="column q-gutter-sm q-my-sm"></div>
      </q-card-section>

      <q-card-actions align="right" class="text-primary">
        <q-btn no-caps flat label="Cancel" v-close-popup />
        <q-btn
          no-caps
          rounded
          color="negative"
          label="Delete"
          @click="
            deleteMessage(message.message.id),
              (modalConfirmation = false),
              $router.push('/messaging')
          "
          v-close-popup
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
